import { Music } from 'lucide-react';
import { AudioPlayer } from '@ai-tutor/ui';
import type { Source } from '@/data/fixtures';
import { useSourceFileUrl } from '@/hooks/lesson/useSources';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface AudioViewerProps {
  source: Source;
  // Legacy callback - kept for compatibility with the older viewer API.
  // Download now lives in the shell toolbar.
  onDownload?: (source: Source) => void;
}

export function AudioViewer({ source }: AudioViewerProps) {
  const { data, isLoading, error } = useSourceFileUrl(source.id);

  if (isLoading) return <ViewerLoading />;
  if (error || !data?.url)
    return (
      <ViewerError
        message="Couldn't load audio"
        detail={(error as Error | undefined)?.message}
      />
    );

  const subtitle =
    source.original_filename && source.original_filename !== source.name
      ? source.original_filename
      : undefined;

  return (
    <ViewerShell sourceId={source.id} icon={Music} label='Audio' tone='notes'>
      <div className='flex h-full items-center justify-center p-6'>
        <div className='w-full max-w-2xl overflow-hidden rounded-2xl border border-border/60'>
          {/* Episode header - podcast "cover" strip (neutral; purple only on
              the small cover badge as a category accent) */}
          <div className='min-w-0 border-b border-border/60 p-6'>
            <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Podcast
            </p>
            <h3 className='truncate text-lg font-semibold text-foreground'>
              {source.name}
            </h3>
            {subtitle && (
              <p className='truncate text-xs text-muted-foreground'>
                {subtitle}
              </p>
            )}
          </div>

          {/* Uses the shared AudioPlayer's default indigo palette so it matches
              the lesson Podcast tab (play button, waveform, slider, thumb). */}
          <div className='h-60 p-4'>
            <AudioPlayer url={data.url} />
          </div>
        </div>
      </div>
    </ViewerShell>
  );
}
